import React, { useState, useMemo, useEffect } from 'react';

export interface CourseWordItem {
  course_word_id: number;
  order_index: number;
  word: string;
  translation: string;
  lesson_index: number;
  status: 'completed' | 'known_from_dict' | 'learning' | 'new';
  is_completed: boolean;
  mc_done: boolean;
  input_done: boolean;
}

export interface LessonItem {
  lesson_index: number;
  title: string;
  range_label: string;
  total_words: number;
  completed_words: number;
  mc_completed_words?: number;
  phase_remaining?: number;
  percent: number;
  is_completed: boolean;
  is_locked?: boolean;
  phase?: 'mc' | 'input' | 'completed';
}

interface MetroTimelineProps {
  lessons: LessonItem[];
  words: CourseWordItem[];
  currentLessonIndex: number;
  color: string;
  onStartLesson: (lessonIndex: number) => void;
  onResetLesson: (lessonIndex: number) => void;
  onViewWords: (lessonIndex: number) => void;
}

export function MetroTimeline({
  lessons,
  words,
  currentLessonIndex,
  color,
  onStartLesson,
  onResetLesson,
  onViewWords,
}: MetroTimelineProps) {
  const isMultiChapter = lessons.length > 15;

  // Группировка в главы при большом количестве уроков
  const chapters = useMemo(() => {
    if (!isMultiChapter) return [];
    const result = [];
    const chunkSize = 10;
    for (let i = 0; i < lessons.length; i += chunkSize) {
      const chunk = lessons.slice(i, i + chunkSize);
      const chapterIndex = Math.floor(i / chunkSize) + 1;
      const chapterTotal = chunk.reduce((s, l) => s + l.total_words, 0);
      const chapterCompleted = chunk.reduce((s, l) => s + l.completed_words, 0);
      const isChapterDone = chunk.every(l => l.is_completed);
      const hasCurrent = chunk.some(l => l.lesson_index === currentLessonIndex);
      const firstL = chunk[0].lesson_index;
      const lastL = chunk[chunk.length - 1].lesson_index;

      result.push({
        chapterIndex,
        title: `Глава ${chapterIndex}: Уроки ${firstL}–${lastL}`,
        lessons: chunk,
        total_words: chapterTotal,
        completed_words: chapterCompleted,
        percent: chapterTotal > 0 ? Math.round((chapterCompleted / chapterTotal) * 100) : 0,
        is_completed: isChapterDone,
        has_current: hasCurrent,
      });
    }
    return result;
  }, [lessons, currentLessonIndex, isMultiChapter]);

  // Состояние развернутых глав
  const [expandedChapters, setExpandedChapters] = useState<Record<number, boolean>>({});

  // Автоматически разворачиваем главу, содержащую текущий урок
  useEffect(() => {
    if (isMultiChapter && chapters.length > 0) {
      const activeCh = chapters.find(c => c.has_current) || chapters[0];
      if (activeCh) {
        setExpandedChapters(prev => ({
          ...prev,
          [activeCh.chapterIndex]: true,
        }));
      }
    }
  }, [isMultiChapter, chapters]);

  const toggleChapter = (chIndex: number) => {
    setExpandedChapters(prev => ({
      ...prev,
      [chIndex]: !prev[chIndex],
    }));
  };

  const scrollToCurrentLesson = () => {
    // Если текущая глава свёрнута — разворачиваем её
    if (isMultiChapter) {
      const activeCh = chapters.find(c => c.has_current);
      if (activeCh && !expandedChapters[activeCh.chapterIndex]) {
        setExpandedChapters(prev => ({ ...prev, [activeCh.chapterIndex]: true }));
      }
    }
    setTimeout(() => {
      const el = document.getElementById('current-lesson-stop');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Слова для каждого урока
  const wordsByLesson = useMemo(() => {
    const map = new Map<number, CourseWordItem[]>();
    for (const w of words) {
      const list = map.get(w.lesson_index) || [];
      list.push(w);
      map.set(w.lesson_index, list);
    }
    return map;
  }, [words]);

  // Рендер одиночного остановочного пункта Metro
  const renderStop = (lesson: LessonItem, isFirst: boolean, isLast: boolean) => {
    const isCompleted = lesson.is_completed;
    const isCurrent = currentLessonIndex === lesson.lesson_index && !isCompleted;
    const isLocked = Boolean(lesson.is_locked && !isCompleted);
    const isMilestone = lesson.lesson_index % 5 === 0;

    const lessonWords = wordsByLesson.get(lesson.lesson_index) || [];
    const previewWords = lessonWords.slice(0, 4).map(w => w.word).join(', ');
    const previewSuffix = lessonWords.length > 4 ? '…' : '';
    const fullPreview = previewWords ? `${previewWords}${previewSuffix}` : lesson.range_label;

    return (
      <div
        key={lesson.lesson_index}
        id={isCurrent ? 'current-lesson-stop' : undefined}
        style={{
          position: 'relative',
          display: 'flex',
          marginBottom: 16,
          opacity: isLocked ? 0.55 : 1,
        }}
      >
        {/* Вертикальная ось Metro слева (ширина 38px) */}
        <div
          style={{
            width: 38,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            flexShrink: 0,
          }}
        >
          {/* Верхняя линия */}
          {!isFirst && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: '50%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 3,
                background: isCompleted ? '#10b981' : isCurrent ? color : 'var(--theme-border)',
                zIndex: 1,
              }}
            />
          )}

          {/* Нижняя линия */}
          {!isLast && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                bottom: -16,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 3,
                background: isCompleted ? '#10b981' : 'var(--theme-border)',
                borderStyle: isLocked ? 'dashed' : 'solid',
                zIndex: 1,
              }}
            />
          )}

          {/* Иконка остановки */}
          <div
            className={isCurrent ? 'pulse-glow' : undefined}
            onClick={() => {
              if (!isLocked) onStartLesson(lesson.lesson_index);
            }}
            style={{
              width: isCurrent ? 36 : isMilestone ? 32 : 28,
              height: isCurrent ? 36 : isMilestone ? 32 : 28,
              borderRadius: '50%',
              background: isCompleted
                ? '#10b981'
                : isCurrent
                  ? color
                  : isMilestone
                    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                    : 'var(--theme-bg-elevated)',
              border: isCurrent
                ? '3px solid #ffffff'
                : isCompleted
                  ? '2px solid rgba(255, 255, 255, 0.4)'
                  : isMilestone
                    ? '2px solid rgba(245, 158, 11, 0.6)'
                    : '2px solid var(--theme-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isCurrent ? 16 : isMilestone ? 15 : 12,
              fontWeight: 800,
              color: isCompleted || isCurrent || isMilestone ? '#ffffff' : 'var(--theme-text-secondary)',
              zIndex: 2,
              cursor: isLocked ? 'not-allowed' : 'pointer',
              boxShadow: isCurrent
                ? `0 0 16px ${color}80`
                : isMilestone
                  ? '0 0 12px rgba(245, 158, 11, 0.5)'
                  : 'none',
              transition: 'transform 0.2s ease',
              marginTop: 10,
            }}
          >
            {isCompleted
              ? '✓'
              : isCurrent
                ? (lesson.phase === 'input' ? '✍️' : '⚔️')
                : isMilestone
                  ? '🎁'
                  : isLocked
                    ? '🔒'
                    : lesson.lesson_index}
          </div>
        </div>

        {/* Правая карточка урока */}
        <div
          style={{
            flex: 1,
            marginLeft: 10,
            background: isCurrent
              ? `linear-gradient(135deg, ${color}16 0%, var(--theme-bg-elevated) 100%)`
              : 'var(--theme-bg-elevated)',
            border: isCurrent
              ? `2px solid ${color}`
              : isCompleted
                ? '1px solid rgba(16, 185, 129, 0.4)'
                : isMilestone
                  ? '1px solid rgba(245, 158, 11, 0.4)'
                  : '1px solid var(--theme-border)',
            borderRadius: 16,
            padding: '12px 14px',
            boxShadow: isCurrent ? `0 4px 18px ${color}25` : '0 2px 8px rgba(0,0,0,0.1)',
            position: 'relative',
          }}
        >
          {/* Верхняя строка карточки */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: isCurrent ? color : 'var(--theme-text)' }}>
                {lesson.title}
              </span>
              <span style={{ fontSize: 11, color: 'var(--theme-text-secondary)', fontWeight: 600 }}>
                ({lesson.completed_words}/{lesson.total_words})
              </span>
            </div>

            {/* Статус-пилл */}
            {isCompleted ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: '#10b981',
                  background: 'rgba(16, 185, 129, 0.12)',
                  padding: '2px 8px',
                  borderRadius: 999,
                }}
              >
                ⭐ Освоен
              </span>
            ) : isCurrent ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: '#ffffff',
                  background: color,
                  padding: '2px 8px',
                  borderRadius: 999,
                  boxShadow: `0 2px 6px ${color}50`,
                }}
              >
                {lesson.phase === 'input' ? '✍️ Шаг 2: Ввод' : '🎯 Шаг 1: Тест'}
              </span>
            ) : isLocked ? (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--theme-text-muted)',
                  background: 'rgba(255, 255, 255, 0.04)',
                  padding: '2px 8px',
                  borderRadius: 999,
                }}
              >
                🔒 Закрыт
              </span>
            ) : null}
          </div>

          {/* Честное превью реальных слов */}
          <div
            style={{
              fontSize: 12,
              color: 'var(--theme-text-secondary)',
              lineHeight: 1.35,
              marginBottom: 8,
            }}
          >
            Слова {lesson.range_label}:{' '}
            <strong style={{ color: 'var(--theme-text)', fontFamily: 'monospace', fontWeight: 600 }}>
              {fullPreview}
            </strong>
          </div>

          {/* Прогресс-бар урока */}
          <div
            style={{
              height: 4,
              background: 'rgba(255, 255, 255, 0.08)',
              borderRadius: 2,
              overflow: 'hidden',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${lesson.percent}%`,
                background: isCompleted ? '#10b981' : color,
                borderRadius: 2,
                transition: 'width 0.3s ease',
              }}
            />
          </div>

          {/* Кнопки действий */}
          {!isLocked && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 4 }}>
              <button
                onClick={() => onStartLesson(lesson.lesson_index)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  background: isCurrent ? color : 'var(--theme-bg-card)',
                  color: isCurrent ? '#ffffff' : 'var(--theme-text)',
                  border: isCurrent ? 'none' : '1px solid var(--theme-border)',
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <span>{isCurrent ? (lesson.phase === 'input' ? '✍️' : '⚔️') : '▶️'}</span>
                <span>
                  {isCurrent
                    ? (lesson.phase === 'input' ? 'Закрепить вводом' : 'Пройти тест')
                    : isCompleted
                      ? 'Повторить урок'
                      : 'Начать урок'}
                </span>
              </button>

              <button
                onClick={() => onViewWords(lesson.lesson_index)}
                style={{
                  padding: '8px 10px',
                  background: 'transparent',
                  color: 'var(--theme-primary)',
                  border: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Слова →
              </button>

              {lesson.completed_words > 0 && (
                <button
                  onClick={() => onResetLesson(lesson.lesson_index)}
                  style={{
                    padding: '8px',
                    background: 'transparent',
                    color: 'var(--theme-text-muted)',
                    border: 'none',
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                  title="Сбросить прогресс урока"
                >
                  🔄
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', paddingBottom: 20 }}>
      {/* Если курс разбит на главы (Full Course) */}
      {isMultiChapter ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {chapters.map(ch => {
            const isOpen = Boolean(expandedChapters[ch.chapterIndex]);

            return (
              <div
                key={ch.chapterIndex}
                style={{
                  background: ch.has_current
                    ? 'linear-gradient(180deg, var(--theme-bg-elevated) 0%, rgba(124, 77, 255, 0.05) 100%)'
                    : 'var(--theme-bg-card)',
                  border: ch.has_current
                    ? `2px solid ${color}`
                    : ch.is_completed
                      ? '1px solid rgba(16, 185, 129, 0.35)'
                      : '1px solid var(--theme-border)',
                  borderRadius: 18,
                  overflow: 'hidden',
                  boxShadow: ch.has_current ? `0 4px 20px ${color}20` : 'none',
                }}
              >
                {/* Заголовок Главы (Аккордеон) */}
                <div
                  onClick={() => toggleChapter(ch.chapterIndex)}
                  style={{
                    padding: '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 10,
                        background: ch.is_completed
                          ? '#10b981'
                          : ch.has_current
                            ? color
                            : 'var(--theme-bg-elevated)',
                        color: ch.is_completed || ch.has_current ? '#fff' : 'var(--theme-text-secondary)',
                        fontSize: 14,
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {ch.is_completed ? '✓' : ch.chapterIndex}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--theme-text)' }}>
                        {ch.title}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--theme-text-secondary)', marginTop: 2 }}>
                        {ch.completed_words} / {ch.total_words} слов ({ch.percent}%)
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {ch.has_current && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 800,
                          color: color,
                          background: `${color}18`,
                          padding: '3px 8px',
                          borderRadius: 999,
                        }}
                      >
                        Текущая
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
                      {isOpen ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {/* Раскрытое содержимое главы — Metro линия */}
                {isOpen && (
                  <div style={{ padding: '8px 14px 14px', borderTop: '1px solid var(--theme-border)' }}>
                    {ch.lessons.map((lesson, idx) =>
                      renderStop(lesson, idx === 0, idx === ch.lessons.length - 1)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* Обычный линейный Express / Top 100 трек */
        <div>
          {lessons.map((lesson, idx) => renderStop(lesson, idx === 0, idx === lessons.length - 1))}
        </div>
      )}

      {/* Плавающая кнопка возврата к текущему уроку */}
      {isMultiChapter && (
        <div
          style={{
            position: 'sticky',
            bottom: 12,
            marginTop: 16,
            zIndex: 30,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <button
            onClick={scrollToCurrentLesson}
            style={{
              padding: '10px 18px',
              background: 'var(--theme-bg-elevated)',
              border: `1.5px solid ${color}`,
              color: color,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>📍</span>
            <span>К текущему уроку: {currentLessonIndex}</span>
          </button>
        </div>
      )}
    </div>
  );
}
